// Shared number/currency/time formatting for the billing UI. Extracted from
// the inline helpers in BillRunDashboard/BuildingReport so Insights reuses the
// exact same en-ZA formatting (and there's now one place to change it).

/**
 * Rands from cents, en-ZA. e.g. 138422 → "R 1 384,22".
 *
 * `maxFractionDigits` exists only for tariff RATE displays, which show up to
 * 4 decimals (R3,2413/kWh). Amounts leave it at the default 2.
 *
 * ------------------------------------------------------------------------
 * The separator after "R" is U+00A0 NO-BREAK SPACE, not U+0020, and that is
 * load-bearing — it is the whole of the "R is above the value" defect.
 *
 * `toLocaleString('en-ZA')` already joins digit groups with U+00A0, so in
 * `R 1 066 522,94` the hand-written ASCII space was the ONLY break
 * opportunity in the entire string. A narrow table column therefore broke
 * there every time — not by preference, but because there was nowhere else
 * to break — orphaning the "R" onto its own line. Code points, before:
 *
 *   0052 0020 0031 00a0 0030 0036 0036 00a0 0035 0032 0032 002c 0039 0034
 *        ^^^^ the only breakable character in the string
 *
 * Kept as a literal rather than switching to
 * `Intl.NumberFormat({ style: 'currency', currency: 'ZAR' })`: ICU's own
 * en-ZA output is identical to this one, U+00A0 included, which is what
 * establishes U+00A0 as the locale-correct separator here. But ICU currency
 * output is CLDR-version dependent and these are customer-facing amounts, so
 * the literal keeps rendering deterministic across engines instead of
 * inheriting whatever CLDR a given browser ships.
 *
 * Consequence for layout: the string is now atomic, so its min-content width
 * is the whole value rather than its longest run. A column rendering it must
 * be able to afford that (~13px more than before) or share a row with
 * something that wraps.
 */
export function fmtCurrency(cents: number, maxFractionDigits = 2): string {
  return `R ${(cents / 100).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: maxFractionDigits,
  })}`;
}

/**
 * As `fmtCurrency`, but renders an absent amount as "-".
 *
 * This is the shape six pages had each hand-rolled inline. Four of those
 * copies omitted `maximumFractionDigits` entirely, which lets Intl's default
 * of 3 through — inert for integer-cent inputs, wrong for anything else.
 */
export function fmtCurrencyOrDash(cents: number | null | undefined, maxFractionDigits = 2): string {
  if (cents === null || cents === undefined) return '-';
  return fmtCurrency(cents, maxFractionDigits);
}

/** Plain number, en-ZA, `d` decimals (default 0). */
export function fmtNum(n: number, d = 0): string {
  return n.toLocaleString('en-ZA', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

// asset_datapoint epochs already carry SAST wall-clock (see
// project-timezone-discipline) — so format them in UTC to render the SAST
// clock as-is. Do NOT add the +2h offset; that would double-shift.
function sastParts(epochMs: number): { date: string; time: string } {
  const d = new Date(epochMs);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`,
    time: `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`,
  };
}

/** SAST "HH:MM" for an interval epoch. */
export function fmtSastTime(epochMs: number): string {
  return sastParts(epochMs).time;
}

/** SAST "MMM d, HH:MM" for an interval epoch (compact axis/tooltip label). */
export function fmtSastDateTime(epochMs: number): string {
  const { date, time } = sastParts(epochMs);
  return `${date} ${time}`;
}
